import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Breadcrumb from "@/components/feature/Breadcrumb";
import NewsCard from "@/components/feature/NewsCard";
import AnimeCard from "@/components/feature/AnimeCard";
import usePageMeta from "@/hooks/usePageMeta";
import { newsList } from "@/mocks/news";
import { animes } from "@/mocks/animes";

type TabKey = "all" | "news" | "anime";

const tabs: { key: TabKey; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "news", label: "动漫资讯" },
  { key: "anime", label: "动漫作品" },
];

export default function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";
  const [keyword, setKeyword] = useState(query);
  const [tab, setTab] = useState<TabKey>("all");

  useEffect(() => {
    setKeyword(query);
  }, [query]);

  usePageMeta(
    query ? `“${query}” 的搜索结果 - 火狼动漫网` : "搜索 - 火狼动漫网",
    "在火狼动漫网中搜索动漫资讯与动漫作品，快速找到你感兴趣的内容。",
  );

  const q = query.trim().toLowerCase();

  const matchedNews = useMemo(() => {
    if (!q) return [];
    return newsList.filter((n) =>
      [n.title, n.summary, n.category, ...n.tags, ...n.content]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [q]);

  const matchedAnime = useMemo(() => {
    if (!q) return [];
    return animes.filter((a) =>
      [a.title, a.synopsis, a.region, a.status, ...a.genres, ...a.tags]
        .join(" ")
        .toLowerCase()
        .includes(q),
    );
  }, [q]);

  const submit = () => {
    const next = keyword.trim();
    if (next) setSearchParams({ q: next });
    else setSearchParams({});
  };

  const totalCount = matchedNews.length + matchedAnime.length;
  const showNews = tab === "all" || tab === "news";
  const showAnime = tab === "all" || tab === "anime";
  const hotAnime = animes.slice(0, 4);
  const hotNews = newsList.slice(0, 3);

  return (
    <div className="w-full px-4 md:px-8 py-8 md:py-10">
      <div className="max-w-7xl mx-auto">
        <Breadcrumb items={[{ label: "搜索" }]} />

        <div className="mt-5 mb-7">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 mb-4">搜索</h1>
          <div className="flex flex-col sm:flex-row gap-3 max-w-2xl">
            <div className="flex items-center gap-2 flex-1 h-12 px-4 rounded-full bg-background-100 border border-background-200 focus-within:border-primary-300 transition-colors">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-lg"></i>
              </span>
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") submit();
                }}
                placeholder="输入动漫名称、资讯关键词..."
                className="flex-1 bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
              />
            </div>
            <button
              type="button"
              onClick={submit}
              className="h-12 px-7 rounded-full bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              搜索
            </button>
          </div>
        </div>

        {query.trim() ? (
          totalCount > 0 ? (
            <>
              <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 mb-6">
                <p className="text-sm text-foreground-600">
                  关于 “<span className="font-bold text-primary-700">{query}</span>” 共找到{" "}
                  <span className="font-bold text-primary-700">{totalCount}</span> 条结果
                  （资讯 {matchedNews.length} · 作品 {matchedAnime.length}）
                </p>
                <div className="flex items-center p-1 rounded-full bg-background-100 border border-background-200 self-start">
                  {tabs.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      onClick={() => setTab(t.key)}
                      className={`px-4 py-1.5 rounded-full text-sm transition-colors whitespace-nowrap cursor-pointer ${
                        tab === t.key
                          ? "bg-primary-600 text-background-50"
                          : "text-foreground-600 hover:text-primary-700"
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {showNews && matchedNews.length > 0 && (
                <section className="mb-10">
                  <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                    动漫资讯
                  </h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-5">
                    {matchedNews.map((item) => (
                      <NewsCard key={item.id} news={item} />
                    ))}
                  </div>
                </section>
              )}

              {showAnime && matchedAnime.length > 0 && (
                <section>
                  <h2 className="text-lg font-bold text-foreground-950 mb-4 flex items-center gap-2">
                    <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                    动漫作品
                  </h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                    {matchedAnime.map((item) => (
                      <AnimeCard key={item.id} anime={item} />
                    ))}
                  </div>
                </section>
              )}
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-14 px-5 flex flex-col items-center text-center">
              <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
                <i className="ri-search-eye-line text-3xl"></i>
              </span>
              <h2 className="text-lg font-bold text-foreground-900 mb-2">
                没有找到与 “{query}” 相关的内容
              </h2>
              <p className="text-sm text-foreground-500 mb-6">
                换个关键词试试，或看看下面的热门推荐
              </p>

              <div className="w-full max-w-4xl text-left">
                <h3 className="text-sm font-semibold text-foreground-800 mb-3">热门作品</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
                  {hotAnime.map((item) => (
                    <AnimeCard key={item.id} anime={item} />
                  ))}
                </div>
                <h3 className="text-sm font-semibold text-foreground-800 mb-3">热门资讯</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {hotNews.map((item) => (
                    <NewsCard key={item.id} news={item} />
                  ))}
                </div>
              </div>

              <Link
                to="/"
                className="mt-8 px-6 h-11 inline-flex items-center rounded-md bg-primary-600 text-background-50 text-sm font-medium hover:bg-primary-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                返回首页
              </Link>
            </div>
          )
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 py-14 px-5 flex flex-col items-center text-center">
            <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
              <i className="ri-search-line text-3xl"></i>
            </span>
            <h2 className="text-lg font-bold text-foreground-900 mb-2">输入关键词开始搜索</h2>
            <p className="text-sm text-foreground-500 mb-8">
              支持搜索动漫名称、类型标签以及资讯标题与内容
            </p>
            <div className="w-full max-w-4xl text-left">
              <h3 className="text-sm font-semibold text-foreground-800 mb-3">热门作品推荐</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {hotAnime.map((item) => (
                  <AnimeCard key={item.id} anime={item} />
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}